import { Icon } from '../components/Icon';
import { useCallback, useEffect, useState } from 'react';
import {
  cancelDeletion,
  deleteNow,
  fetchDeletions,
  requestDeletion,
  StaffError,
  type PendingDeletion,
} from '../lib/staffClient';

export function DeletionsPage({ onUnauthorized }: { onUnauthorized: () => void }) {
  const [rows, setRows] = useState<PendingDeletion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const [email, setEmail] = useState('');

  const fail = useCallback(
    (err: unknown) => {
      if (err instanceof StaffError && err.status === 401) onUnauthorized();
      else setError(err instanceof StaffError ? err.message : 'Something went wrong.');
    },
    [onUnauthorized],
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows((await fetchDeletions()).deletions);
    } catch (err) {
      fail(err);
    }
  }, [fail]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(id: string, fn: () => Promise<string | void>) {
    setBusyId(id);
    setError(null);
    setNotice(null);
    try {
      const msg = await fn();
      if (msg) setNotice(msg);
      setConfirming(null);
      setTyped('');
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <header className="staff-top">
        <h1 className="skf-title">Deletion requests</h1>
        <button
          className="btn btn--ghost btn--icon"
          aria-label="Refresh"
          title="Refresh"
          onClick={() => void load()}
        >
          <Icon name="arrow-clockwise" />
        </button>
      </header>
      <p className="skf-meta">
        Accounts in the 30-day grace period. The nightly job scrubs them when it ends. “Delete now”
        runs that same scrub immediately and cannot be undone.
      </p>
      <form
        className="staff-addform"
        onSubmit={(e) => {
          e.preventDefault();
          const addr = email.trim();
          if (!addr) return;
          void run('add', async () => {
            await requestDeletion(addr);
            setEmail('');
            return `Deletion started for ${addr}.`;
          });
        }}
      >
        <input
          className="staff-input"
          type="email"
          required
          aria-label="Email of the account to delete"
          placeholder="Email from the user's request"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button className="btn" disabled={busyId === 'add'}>
          <Icon name="person-plus" /> Add request
        </button>
      </form>
      {error && (
        <p className="staff-error" role="alert">
          {error}
        </p>
      )}
      {notice && <p className="skf-meta">{notice}</p>}
      {!rows && !error && <p className="skf-meta">Loading…</p>}
      {rows && rows.length === 0 && <p className="skf-meta">No pending deletions.</p>}
      <div className="staff-list">
        {rows?.map((u) => (
          <article key={u.id} className="staff-tile">
            <p className="staff-tile__label">{u.display_name || u.id}</p>
            <p className="staff-tile__sub">
              {u.email} · requested{' '}
              {new Date(u.requested_at.replace(' ', 'T')).toLocaleDateString()} ·{' '}
              {u.due
                ? 'grace period over — next nightly run'
                : `${u.days_remaining ?? '?'} days remaining`}
            </p>
            {confirming === u.id ? (
              <form
                className="staff-actions"
                onSubmit={(e) => {
                  e.preventDefault();
                  void run(u.id, async () => {
                    const r = await deleteNow(u.id);
                    return r.vendor_pending
                      ? 'Scrubbed locally; vendor erasure failed and will retry nightly.'
                      : 'Account deleted.';
                  });
                }}
              >
                <input
                  className="staff-input"
                  aria-label="Type DELETE to confirm"
                  placeholder="Type DELETE"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                />
                <button
                  className="btn btn--danger"
                  disabled={typed !== 'DELETE' || busyId === u.id}
                >
                  Delete permanently
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => {
                    setConfirming(null);
                    setTyped('');
                  }}
                >
                  Never mind
                </button>
              </form>
            ) : (
              <div className="staff-actions">
                <button
                  className="btn btn--ghost"
                  disabled={busyId === u.id}
                  onClick={() => void run(u.id, async () => void (await cancelDeletion(u.id)))}
                >
                  <Icon name="arrow-counterclockwise" /> Cancel deletion
                </button>
                <button className="btn btn--danger" onClick={() => setConfirming(u.id)}>
                  <Icon name="trash" /> Delete now…
                </button>
              </div>
            )}
          </article>
        ))}
      </div>
    </>
  );
}
